import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BottomNavigation, BottomNavigationAction, Paper, Tooltip, useMediaQuery } from '@mui/material';
import { styled, alpha, type Theme } from '@mui/material/styles';
import {
    Dashboard,
    BookmarkAdded,
    Quiz,
    VideoCall,
    OndemandVideo,
    Rule,
    EventAvailable,
    MoreHoriz,
    Person,
} from '@mui/icons-material';
import { useAuth } from '../../services/common/auth-context';
import { getShellGlow } from '../../shared/shell-accent';

interface TabDef {
    segment: string;
    labelKey: string;
    icon: React.ReactElement;
}

// Pinned as the most frequent daily destinations. No "More" tab for students at all — with the
// catalog pages merged into Courses/Exams (each now carries its own "Browse All" tab, see
// my-courses-page.tsx/my-exams-page.tsx) and My Live Rooms already pinned, My Profile was the
// only thing ever left behind "More" for a student, so it's pinned directly instead of hiding
// the one destination that was actually there behind an extra tap. Labels read "Courses"/"Exams"
// (key names stay `nav.myCourses`/`nav.myExams` — kept distinct from ADMIN_TABS's `nav.courses`/
// `nav.exams` below on purpose, so the two can't drift into meaning the same string by accident
// even though they render the same word today) since the pages are no longer enrolled-only.
// `nav.myProfileShort` (not `nav.myProfile`, which the desktop sidebar/page title still use) is a
// one-word label ("Profile"/"ملفي") — this narrow tab column has no room for the two-word Arabic
// "ملفي الشخصي" without wrapping or truncating.
export const STUDENT_TABS: TabDef[] = [
    { segment: '', labelKey: 'nav.home', icon: <Dashboard /> },
    { segment: 'my-courses', labelKey: 'nav.myCourses', icon: <BookmarkAdded /> },
    { segment: 'my-exams', labelKey: 'nav.myExams', icon: <Quiz /> },
    { segment: 'my-live-rooms', labelKey: 'nav.rooms', icon: <VideoCall /> },
    { segment: 'my-profile', labelKey: 'nav.myProfileShort', icon: <Person /> },
];

// Admin has ~15 routes — these 3 are pinned as the most frequent daily destinations,
// everything else lives behind "More" (see more-page.tsx).
export const ADMIN_TABS: TabDef[] = [
    { segment: '', labelKey: 'nav.home', icon: <Dashboard /> },
    { segment: 'courses', labelKey: 'nav.courses', icon: <OndemandVideo /> },
    { segment: 'exams', labelKey: 'nav.exams', icon: <Rule /> },
    { segment: 'attendance', labelKey: 'nav.attendanceShort', icon: <EventAvailable /> },
    { segment: 'more', labelKey: 'nav.more', icon: <MoreHoriz /> },
];

export const BOTTOM_NAV_HEIGHT = 60;

const StyledPaper = styled(Paper)(({ theme }) => ({
    position: 'fixed',
    insetInlineStart: 0,
    insetInlineEnd: 0,
    bottom: 0,
    zIndex: theme.zIndex.appBar,
    backdropFilter: 'blur(16px)',
    backgroundColor: alpha(theme.palette.background.paper, 0.85),
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
    boxShadow: `0 -4px 20px -8px ${getShellGlow(theme, 0.18)}`,
    paddingBottom: 'env(safe-area-inset-bottom)',
    ...theme.applyStyles('dark', {
        backgroundColor: alpha('#1E1E2F', 0.85),
    }),
}));

const StyledBottomNavigation = styled(BottomNavigation)(({ theme }) => ({
    backgroundColor: 'transparent',
    height: BOTTOM_NAV_HEIGHT,
    '& .MuiBottomNavigationAction-root': {
        minWidth: 0,
        padding: theme.spacing(1, 0.5),
        color: theme.vars.palette.text.secondary,
        transition: 'color 0.2s ease, transform 0.2s ease',
        '&.Mui-selected': {
            color: theme.vars.palette.primary.main,
            transform: 'translateY(-2px)',
        },
    },
    '& .MuiBottomNavigationAction-label': {
        fontSize: '0.7rem',
        '&.Mui-selected': {
            fontSize: '0.72rem',
            fontWeight: 600,
        },
    },
}));

export default function BottomNavBar() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    if (!isMobile) {
        return null;
    }

    const tabs = user?.isStudent ? STUDENT_TABS : ADMIN_TABS;
    const currentSegment = location.pathname.split('/').filter(Boolean)[0] || '';
    const activeValue = tabs.some((tab) => tab.segment === currentSegment) ? currentSegment : false;

    return (
        <StyledPaper elevation={0}>
            <StyledBottomNavigation
                value={activeValue}
                onChange={(_event, newValue: string) => navigate(`/${newValue}`)}
                showLabels
            >
                {tabs.map((tab) => (
                    <Tooltip key={tab.segment || 'dashboard'} title={t(tab.labelKey)}>
                        <BottomNavigationAction
                            value={tab.segment}
                            label={t(tab.labelKey)}
                            icon={tab.icon}
                        />
                    </Tooltip>
                ))}
            </StyledBottomNavigation>
        </StyledPaper>
    );
}
